import { optimizeImage } from './fotobook_image_processor.js';
import { DEFAULT_ENABLED_IMAGE_FORMATS, IMAGE_FORMATS } from './image_formats.js';

export default class FotobookPhotoSelector {
  constructor() {
    console.log('FotobookPhotoSelector: Constructor called');
    this.selectedFiles = [];
    this.maxFiles =
      window.TOTAL_PHOTOS ||
      ((window.FOTOBOOK_CONFIG?.totalSheets || 5) * 4) ||
      20;
    this.concurrencyLimit = 3; // Límite de subidas/procesos simultáneos
    this.activeUploads = 0;
    this.uploadQueue = [];
    this.supportedFormats = new Set(DEFAULT_ENABLED_IMAGE_FORMATS);
    this.allowedExtensions = new Set();
    this.allowedMimeTypes = new Set();
    this.refreshAllowedImageTypes();
    
    // Inicializar Worker para procesamiento fuera del hilo principal
    this.initWorker();
    
    console.log(`FotobookPhotoSelector: Max files (pages) set to ${this.maxFiles}`);
    this.init();
  }

  initWorker() {
    try {
      // Verificar soporte de Worker y OffscreenCanvas
      if (window.Worker && window.OffscreenCanvas) {
        // En Vite/Rails, esta es la forma estándar de cargar un Worker
        this.worker = new Worker(new URL('./image_worker.js', import.meta.url), { type: 'module' });
        this.workerCallbacks = new Map();
        
        this.worker.onmessage = (e) => {
          const { success, blob, width, height, id, error } = e.data;
          const callback = this.workerCallbacks.get(id);
          if (callback) {
            if (success) callback.resolve({ blob, width, height });
            else callback.reject(new Error(error));
            this.workerCallbacks.delete(id);
          }
        };

        this.worker.onerror = (err) => {
          console.error('FotobookPhotoSelector Worker Error:', err);
          this.worker = null; // Fallback a hilo principal en caso de error crítico
        };
      }
    } catch (err) {
      console.warn('FotobookPhotoSelector: Web Worker no disponible, usando hilo principal.', err);
      this.worker = null;
    }
  }

  async optimizeImageWithWorker(file, options) {
    if (!this.worker) {
      // Fallback al hilo principal si el worker no está disponible
      return optimizeImage(file, options);
    }

    const id = Math.random().toString(36).substring(2, 11);
    try {
      return await new Promise((resolve, reject) => {
        this.workerCallbacks.set(id, { resolve, reject });
        this.worker.postMessage({
          file,
          maxDimension: options.maxDimension || 2500,
          quality: options.quality || 0.8,
          id
        });

        // Timeout de seguridad por si el worker se bloquea
        setTimeout(() => {
          if (this.workerCallbacks.has(id)) {
            this.workerCallbacks.delete(id);
            reject(new Error('Timeout de procesamiento en Worker'));
          }
        }, 30000);
      });
    } catch (error) {
      const message = (error && error.message ? error.message : '').toLowerCase();
      const shouldFallbackToMainThread =
        message.includes('decode') ||
        message.includes('source image') ||
        message.includes('imagebitmap') ||
        message.includes('offscreen');

      if (shouldFallbackToMainThread) {
        console.warn('FotobookPhotoSelector: Worker falló al decodificar; usando hilo principal.', error);
        return optimizeImage(file, options);
      }

      throw error;
    }
  }

  init() {
    this.initializeFileInput();
    this.hydratePersistedLibrary();
    this.updateFileCounter();
    this.updateFileList();
  }

  refreshAllowedImageTypes() {
    this.allowedExtensions = new Set();
    this.allowedMimeTypes = new Set();

    this.supportedFormats.forEach((formatKey) => {
      const format = IMAGE_FORMATS[formatKey];
      if (!format) return;

      format.extensions.forEach((extension) => this.allowedExtensions.add(extension));
      format.mimeTypes.forEach((mimeType) => this.allowedMimeTypes.add(mimeType));
    });
  }

  getAcceptAttributeValue() {
    const acceptValues = [];

    this.supportedFormats.forEach((formatKey) => {
      const format = IMAGE_FORMATS[formatKey];
      if (!format) return;

      format.mimeTypes.forEach((mimeType) => acceptValues.push(mimeType));
      format.extensions.forEach((extension) => acceptValues.push(`.${extension}`));
    });

    return Array.from(new Set(acceptValues)).join(',');
  }

  updateInputAccept() {
    if (!this.fileInput) return;
    this.fileInput.setAttribute('accept', this.getAcceptAttributeValue());
  }

  getPlatformInfo() {
    const userAgent = navigator.userAgent || '';
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    const vendor = navigator.vendor || '';

    const isWindows = /win/i.test(platform) || /windows/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent) || (/mac/i.test(platform) && navigator.maxTouchPoints > 1);
    const isMac = !isIOS && (/mac/i.test(platform) || /mac os/i.test(userAgent));
    const isWebKit = /applewebkit/i.test(userAgent) && !/chrome|chromium|edg|opr|opera/i.test(userAgent);
    const isSafari = /safari/i.test(userAgent) && /apple/i.test(vendor) && !/chrome|chromium|crios|fxios|edg|opr|opera/i.test(userAgent);

    return {
      isWindows,
      isIOS,
      isMac,
      isWebKit,
      isSafari
    };
  }

  async detectMimeTypeSupport(mimeType) {
    if (!mimeType) return null;

    if (window.ImageDecoder && typeof window.ImageDecoder.isTypeSupported === 'function') {
      try {
        return await window.ImageDecoder.isTypeSupported(mimeType);
      } catch (error) {
        console.warn(`FotobookPhotoSelector: no se pudo consultar soporte para ${mimeType}`, error);
      }
    }

    return null;
  }

  async resolveConditionalFormatSupport() {
    const [heicSupport, heifSupport, avifSupport] = await Promise.all([
      this.detectMimeTypeSupport('image/heic'),
      this.detectMimeTypeSupport('image/heif'),
      this.detectMimeTypeSupport('image/avif')
    ]);

    const platform = this.getPlatformInfo();
    const fallbackHeicSupport = platform.isIOS || (platform.isMac && platform.isSafari) || platform.isWebKit;

    return {
      heic: heicSupport === null ? fallbackHeicSupport : heicSupport,
      heif: heifSupport === null ? fallbackHeicSupport : heifSupport,
      avif: avifSupport === null ? this.supportedFormats.has('avif') : avifSupport
    };
  }

  async configureSupportedImageFormats() {
    try {
      const support = await this.resolveConditionalFormatSupport();
      const enabledFormats = new Set(DEFAULT_ENABLED_IMAGE_FORMATS);

      if (!support.avif) {
        enabledFormats.delete('avif');
      }

      if (support.heic) enabledFormats.add('heic');
      if (support.heif) enabledFormats.add('heif');

      this.supportedFormats = enabledFormats;
      this.refreshAllowedImageTypes();
      this.updateInputAccept();
    } catch (error) {
      console.warn('FotobookPhotoSelector: usando formatos de imagen por defecto.', error);
      this.refreshAllowedImageTypes();
      this.updateInputAccept();
    }
  }

  isFileTypeAllowed(file) {
    if (!file) return false;

    const type = typeof file.type === 'string' ? file.type.toLowerCase() : '';
    if (type && this.allowedMimeTypes.has(type)) return true;

    const name = (file.name || '').toLowerCase();
    const extension = name.includes('.') ? name.split('.').pop() : '';
    if (extension && this.allowedExtensions.has(extension)) return true;

    return false;
  }

  resolveMaxFiles(fileInput = null) {
    const fromDataset = fileInput ? parseInt(fileInput.dataset.maxFiles, 10) : NaN;
    if (Number.isInteger(fromDataset) && fromDataset > 0) return fromDataset;

    const fromWindow = parseInt(window.TOTAL_PHOTOS, 10);
    if (Number.isInteger(fromWindow) && fromWindow > 0) return fromWindow;

    const fromConfig = parseInt(window.FOTOBOOK_CONFIG?.totalSheets, 10);
    if (Number.isInteger(fromConfig) && fromConfig > 0) return fromConfig * 4;

    return 20;
  }

  isImageFile(file) {
    if (!file) return false;
    return this.isFileTypeAllowed(file);
  }

  initializeFileInput() {
    const fileInput = document.querySelector('#photos-input') || document.querySelector('.input-files');
    if (!fileInput) return;

    // Guardar referencia para poder resetear el valor tras procesar
    this.fileInput = fileInput;

    // Priorizar el máximo definido en el input para mantener consistencia con la vista.
    this.maxFiles = this.resolveMaxFiles(fileInput);

    // Configurar el input de archivos
    fileInput.addEventListener('change', (e) => {
      // Tomar snapshot inmediato del FileList para evitar comportamientos inconsistentes entre navegadores.
      const selectedFiles = Array.from(e?.target?.files || []);
      this.handleFileSelection(selectedFiles);
    });

    // Configurar un accept conservador y actualizarlo según la capacidad real del navegador.
    this.updateInputAccept();
    fileInput.multiple = true;

    this.configureSupportedImageFormats();
  }

  hydratePersistedLibrary() {
    const libraryPhotos = Array.isArray(window.FOTOBOOK_LIBRARY_PHOTOS)
      ? window.FOTOBOOK_LIBRARY_PHOTOS
      : [];

    if (libraryPhotos.length === 0) {
      console.log('FotobookPhotoSelector: No persisted library photos found');
      return;
    }

    console.log('FotobookPhotoSelector: Hydrating persisted library', {
      photosCount: libraryPhotos.length
    });

    libraryPhotos.forEach((photo) => {
      const persistedId = parseInt(photo?.id, 10);
      if (Number.isNaN(persistedId)) return;

      const alreadyLoaded = this.selectedFiles.some((selectedPhoto) => {
        return parseInt(selectedPhoto?.id, 10) === persistedId;
      });

      if (alreadyLoaded) return;

      this.selectedFiles.push({
        id: persistedId,
        status: 'ready',
        file: null,
        fileName: photo?.name || `Foto ${persistedId}`,
        localUrl: null,
        thumbUrl: photo?.thumbUrl || photo?.thumb_url || photo?.url || null,
        url: photo?.url || photo?.thumbUrl || photo?.thumb_url || null,
        persisted: true
      });
    });

    this.checkPrintButton();
  }

  handleFileSelection(files) {
    const fileArray = Array.isArray(files) ? files : Array.from(files || []);
    const validFiles = fileArray.filter(file => {
      console.log(`FotobookPhotoSelector: Validating file ${file.name} with type ${file.type}`);
      return this.isImageFile(file);
    });
    const invalidFilesCount = fileArray.length - validFiles.length;

    if (invalidFilesCount > 0) {
      this.showAlert('Algunas fotos no se agregaron porque su formato no es compatible en este navegador.');
    }

    this.maxFiles = this.resolveMaxFiles(this.fileInput);

    console.log('FotobookPhotoSelector: Adding files to library', {
      validFilesCount: validFiles.length,
      currentLibrarySize: this.selectedFiles.length,
      recommendedSlots: this.maxFiles
    });

    const filesToAdd = validFiles;
    // Agregar archivos con estado 'uploading'
    filesToAdd.forEach(file => {
      const photoObj = {
        id: null,
        status: 'uploading',
        file: file,
        fileName: file.name,
        localUrl: URL.createObjectURL(file),
        thumbUrl: null,
        url: null
      };
      this.selectedFiles.push(photoObj);
      this.enqueueUpload(photoObj);
    });

    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();

    if (this.fileInput) this.fileInput.value = '';
  }

  enqueueUpload(photoObj) {
    this.uploadQueue.push(photoObj);
    this.processQueue();
  }

  async processQueue() {
    if (this.activeUploads >= this.concurrencyLimit || this.uploadQueue.length === 0) {
      return;
    }

    this.activeUploads++;
    const photoObj = this.uploadQueue.shift();

    try {
      // 1. Optimizar cliente (convertir a JPG y redimensionar) usando Worker
      const optimized = await this.optimizeImageWithWorker(photoObj.file, { maxDimension: 2500, quality: 0.8 });
      
      // 2. Subir al servidor
      await this.uploadFile(photoObj, optimized.blob);
    } catch (error) {
      console.error('Processing/Upload failed:', error);
      photoObj.status = 'error';
      this.showAlert('Error al procesar/subir una foto');
    } finally {
      this.activeUploads--;
      this.updateFileList();
      this.checkPrintButton();
      // Continuar con la cola
      this.processQueue();
    }
  }

  async uploadFile(photoObj, blob) {
    const formData = new FormData();
    // Usar el blob optimizado, o el original si falló la optimización (aunque optimizeImage debería resolver siempre)
    formData.append('file', blob || photoObj.file, photoObj.file.name.replace(/\.[^/.]+$/, "") + ".jpg");

    try {
      console.log('FotobookPhotoSelector: Uploading file', {
        originalName: photoObj.file?.name,
        optimizedBlobSize: blob?.size
      });
      const response = await fetch('/photos/upload', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      });

      if (!response.ok) throw new Error('Error en el servidor');

      const data = await response.json();
      console.log('FotobookPhotoSelector: Upload completed', {
        originalName: photoObj.file?.name,
        photoId: data.id,
        thumbUrl: data.thumb_url,
        url: data.url
      });
      photoObj.id = data.id;
      photoObj.status = 'ready';
      photoObj.thumbUrl = data.thumb_url;
      photoObj.url = data.url;
    } catch (error) {
      throw error; // Capturado por processQueue
    }
  }

  updateFileCounter() {
    const counter = document.querySelector('#photos-selected-counter');
    if (counter) {
      counter.textContent = `${this.selectedFiles.length}`;
    }
  }

  updateFileList() {
    const fileList = document.querySelector('#selected-files-list');
    if (!fileList) return;

    fileList.innerHTML = '';
    fileList.classList.add('grid', 'grid-cols-3', 'gap-2');

    const usedIndices = window.fotobookEditor && window.fotobookEditor.getUsedFileIndices 
      ? window.fotobookEditor.getUsedFileIndices() 
      : [];

    this.selectedFiles.forEach((photo, index) => {
      const isUsed = usedIndices.includes(index);
      const fileItem = document.createElement('div');
      fileItem.className = `relative overflow-hidden rounded bg-gray-100 flex items-center justify-center transition-all duration-300 ${isUsed ? 'ring-2 ring-green-500 ring-offset-1 opacity-80' : 'hover:ring-2 hover:ring-petrol-300'}`;
      fileItem.style.aspectRatio = '1 / 1';

      if (photo.status === 'uploading') {
        fileItem.innerHTML = `
          <div class="flex flex-col items-center justify-center text-gray-500">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-petrol-500 mb-1"></div>
            <span class="text-[10px]">Cargando...</span>
          </div>
          <button type="button" class="absolute top-1 right-1 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center" onclick="window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[10px]"></i>
          </button>
        `;
      } else if (photo.status === 'ready') {
        fileItem.innerHTML = `
          <img src="${photo.thumbUrl || photo.url || photo.localUrl}" alt="${photo.fileName || `Foto ${index + 1}`}" class="cursor-pointer w-full h-full block object-cover" draggable="true" data-file-index="${index}" />
          ${isUsed ? '<div class="absolute inset-0 bg-green-500/20 flex items-center justify-center pointer-events-none"><i class="fas fa-check-circle text-white text-xl drop-shadow-md"></i></div>' : ''}
          <button type="button" class="absolute top-0.5 right-0.5 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-4 h-4 flex items-center justify-center z-10" onclick="event.stopPropagation(); window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[8px]"></i>
          </button>
        `;

        const imgEl = fileItem.querySelector('img');
        if (imgEl) {
          // Click para agregar al primer slot vacío
          imgEl.addEventListener('click', () => {
            if (window.fotobookEditor && window.fotobookEditor.addPhotoToFirstEmptySlot) {
              window.fotobookEditor.addPhotoToFirstEmptySlot(index);
            }
          });

          // Drag para colocar en slot específico
          imgEl.addEventListener('dragstart', (ev) => {
            try {
              ev.dataTransfer.setData('text/fotobook-file-index', String(index));
              ev.dataTransfer.effectAllowed = 'copy';
            } catch (_) {}
          });
        }
      } 
else if (photo.status === 'error') {
        fileItem.innerHTML = `
          <div class="flex flex-col items-center justify-center text-red-500 text-center p-1">
            <i class="fas fa-exclamation-triangle mb-1"></i>
            <span class="text-[10px]">Error</span>
          </div>
          <button type="button" class="absolute top-1 right-1 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center" onclick="window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[10px]"></i>
          </button>
        `;
      }

      fileList.appendChild(fileItem);
    });
  }

  removeFile(index) {
    const [removed] = this.selectedFiles.splice(index, 1);
    if (removed?.localUrl) {
      URL.revokeObjectURL(removed.localUrl);
    }
    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();
  }

  checkPrintButton() {
    if (window.fotobookEditor?.updateSendToPrintButtonState) {
      console.log(
        'FotobookPhotoSelector: Delegating print button state update to FotobookEditor'
      );
      window.fotobookEditor.updateSendToPrintButtonState();
      return;
    }

    const printButton = document.getElementById('fotobook-send-to-print-btn');
    if (!printButton) return;

    const readyCount = this.selectedFiles.filter(p => p.status === 'ready').length;
    console.log('FotobookPhotoSelector: checkPrintButton fallback', {
      readyCount,
      maxFiles: this.maxFiles
    });
    
    if (readyCount === this.maxFiles) {
      printButton.classList.remove('disabled');
      printButton.disabled = false;
    } else {
      printButton.classList.add('disabled');
      printButton.disabled = true;
    }
  }

  showAlert(message) {
    if (window.appAlerts?.error) {
      window.appAlerts.error(message);
      return;
    }

    alert(message);
  }

  getSelectedFiles() {
    return this.selectedFiles;
  }

  clearFiles() {
    this.selectedFiles.forEach((photo) => {
      if (photo?.localUrl) {
        URL.revokeObjectURL(photo.localUrl);
      }
    });
    this.selectedFiles = [];
    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();
  }
} 
